import React, { useEffect } from 'react';
import type { DrawerProps } from '@archipro-design/aria';
import {
    Button,
    Caption,
    Drawer,
    Flex,
    TileImage,
    pxToRem,
    useAppDisplayModeContext,
    useStyles,
} from '@archipro-design/aria';
import { useLoaderData } from 'react-router';
import type {
    ProductPageLoaderData,
    loader,
} from '~/routes/_app.product.$urlSegment';
import * as S from './ProductLocationsDrawer.style';
import { CloseLine } from '@archipro-design/icons';
import { ProductLocation } from './ProductLocation';
import { useContactLocationMap } from '@modules/professional/hook/use-contact-location-map';
import VariantPricing from '../variant-pricing/VariantPricing';
import { useVariantSelectionState } from '../../context/VariantSelectionContext';
import BottomDrawer from '~/modules/detail/components/bottom-drawer/BottomDrawer';
import { useLatest } from 'ahooks';
import useVariantPrice from '../../hook/use-variant-price';
import { useTracker } from '@archipro-website/tracker';

interface ProductLocationsDrawerProps extends Pick<DrawerProps, 'open'> {
    product: ProductPageLoaderData['product'];
    toggleModal: (value: boolean) => void;
}

const ProductLocationsDrawer = ({
    product,
    toggleModal,
    open,
}: ProductLocationsDrawerProps) => {
    const { css } = useStyles();
    const { showrooms, linkedProfiles } = useLoaderData<typeof loader>();
    const { selectedVariant } = useVariantSelectionState();
    const { mobile } = useAppDisplayModeContext();
    const tracker = useTracker();

    const { hasPrice: hasVariantPrice } = useVariantPrice(
        product.Pricing.PricingType,
    );

    let imageSrc = '';
    let selectedImage = null;
    if (selectedVariant) {
        selectedImage = product.Images.edges.find(
            (node) => node.node.ID === selectedVariant.Image?.ID,
        );
    }
    if (!selectedImage) {
        selectedImage = product.Images.edges[0];
    }
    if (selectedImage) {
        imageSrc =
            selectedImage.node.LibraryImageFile?.DetailPageSquare.Desktop ?? '';
    }

    const { calculateDistance, filteredLocations } = useContactLocationMap(
        showrooms,
        undefined,
        true,
        product.Professional.Country,
    );

    const calculateDistanceRef = useLatest(calculateDistance);

    useEffect(() => {
        if (!open) return;
        calculateDistanceRef.current();
        tracker.log('WhereToBuyDrawerOpen', {
            url: new URL(window.location.href),
        });
    }, [calculateDistanceRef, open, tracker]);

    const content = (
        <Flex className={css(S.ContentContainer)} column>
            <Button
                className={css(S.CloseButton)}
                onClick={() => toggleModal(false)}
                size={20}
                variant="text"
                variables={{
                    iconPadding: 0,
                }}
                icon={<CloseLine />}
            />
            <Flex className={css(S.TileContainer)} column>
                <TileImage
                    images={[
                        {
                            src: imageSrc,
                            alt: `${product.Title} thumbnail`,
                        },
                    ]}
                    height={pxToRem(204)}
                    width={pxToRem(204)}
                    hideFavourite={true}
                />
                <Caption variant="02" className={css(S.ProfessionalTitle)}>
                    {product.Professional.Title}
                </Caption>
                <Caption variant="03" className={css(S.ProductTitle)}>
                    {product.Title}
                </Caption>
                {hasVariantPrice && (
                    <VariantPricing pricingType={product.Pricing.PricingType} />
                )}
            </Flex>
            <Flex className={css(S.LocationContainer)} column>
                <Caption variant="03" className={css(S.LocationTitle)}>
                    This product might be available in the following locations:
                </Caption>
                <Flex className={css(S.LocationList)} column>
                    {filteredLocations.map((showroom, index) => {
                        return (
                            <ProductLocation
                                key={index}
                                location={showroom}
                                professional={
                                    linkedProfiles.find(
                                        (linkedProfile) =>
                                            linkedProfile.ID ===
                                            showroom.professionalID,
                                    ) ?? product.Professional
                                }
                                masterProfessional={product.Professional}
                            />
                        );
                    })}
                </Flex>
            </Flex>
        </Flex>
    );

    if (mobile) {
        return (
            <BottomDrawer
                toggleModal={toggleModal}
                open={!!open}
                className={css(S.MobileDrawer)}
                title="where to buy"
            >
                {content}
            </BottomDrawer>
        );
    }

    return (
        <Drawer
            onOpenChange={(_, data) => toggleModal(!!data?.open)}
            open={open}
            fluid={true}
            anchor={'right'}
            content={{
                styles: { height: '100%' },
                content,
            }}
        />
    );
};

export default ProductLocationsDrawer;
